import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Link from '@mui/material/Link'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useSelector } from 'react-redux'

import SubmitButton from '@/app/component/form/submit-button'
import { Option } from '@/app/component/form/types'
import { useSendRequest } from '@/app/module/dashboard/hooks/use-send-request'
import { selectCountries } from '@/app/module/utils/store/selectors'
import { openClearFeed } from '@/app/service/clearfeed/utils'

export enum RequestType {
  SmsAccess = 'sms-access',
  TwoWaySms = 'two-way-sms',
  VoiceAccess = 'voice-access',
  WhatsApp = 'whatsapp',
  Cati = 'cati',
}

type Props = {}

const RequestForm: React.FC<Props> = () => {
  const countriesSelector = useSelector(selectCountries) as Option[]
  const countries = React.useMemo(() => countriesSelector.map((c) => c.label).sort(), [countriesSelector])
  const [sendRequest, { isLoading: isSending }] = useSendRequest()
  const [requestType, setRequestType] = React.useState<RequestType | ''>('')
  const [countryByType, setCountryByType] = React.useState<Record<RequestType, string | null>>({
    [RequestType.SmsAccess]: null,
    [RequestType.TwoWaySms]: null,
    [RequestType.VoiceAccess]: null,
    [RequestType.WhatsApp]: null,
    [RequestType.Cati]: null,
  })

  const requiresCountry: Record<RequestType, boolean> = React.useMemo(
    () => ({
      [RequestType.SmsAccess]: true,
      [RequestType.TwoWaySms]: true,
      [RequestType.VoiceAccess]: true,
      [RequestType.WhatsApp]: false,
      [RequestType.Cati]: true,
    }),
    [],
  )

  const setCountryFor = (type: RequestType, val: string | null) =>
    setCountryByType((prev) => ({ ...prev, [type]: val }))

  const renderCountrySelector = (type: RequestType) => (
    <Box ml={4} mt={1}>
      <Autocomplete
        options={countries}
        value={countryByType[type]}
        onChange={(_, val) => setCountryFor(type, val)}
        renderInput={(params) => <TextField {...params} label="Country" fullWidth size="small" />}
      />
    </Box>
  )

  const selectedCountry = requestType ? countryByType[requestType as RequestType] : null
  const canSubmit = Boolean(
    requestType && (!requiresCountry[requestType as RequestType] || (selectedCountry && selectedCountry.length > 0)),
  )

  const openChat = () => openClearFeed()

  const handleSubmit = async () => {
    if (!requestType) return
    const rt = requestType as RequestType
    const needsCountry = requiresCountry[rt]
    const chosen = countryByType[rt]
    if (needsCountry && !chosen) return

    const channelLabelMap: Record<RequestType, string> = {
      [RequestType.SmsAccess]: 'SMS access',
      [RequestType.TwoWaySms]: '2-way SMS',
      [RequestType.VoiceAccess]: 'Voice access',
      [RequestType.WhatsApp]: 'WhatsApp Business number',
      [RequestType.Cati]: 'CATI routes',
    }

    const channel = channelLabelMap[rt]
    await sendRequest({
      message: channel,
      country: needsCountry ? chosen : null,
    })

    setRequestType('')
    if (needsCountry) setCountryFor(rt, null)
  }

  return (
    <Box>
      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          name="request-type"
          value={requestType}
          onChange={(e) => setRequestType(e.target.value as RequestType)}
        >
          <FormControlLabel value={RequestType.SmsAccess} control={<Radio />} label="SMS access for" />
          {requestType === RequestType.SmsAccess && renderCountrySelector(RequestType.SmsAccess)}

          <FormControlLabel value={RequestType.TwoWaySms} control={<Radio />} label="2-way SMS in" />
          {requestType === RequestType.TwoWaySms && renderCountrySelector(RequestType.TwoWaySms)}

          <FormControlLabel value={RequestType.VoiceAccess} control={<Radio />} label="Voice access in" />
          {requestType === RequestType.VoiceAccess && renderCountrySelector(RequestType.VoiceAccess)}

          <FormControlLabel value={RequestType.WhatsApp} control={<Radio />} label="A WhatsApp Business number" />

          <FormControlLabel value={RequestType.Cati} control={<Radio />} label="CATI routes to a new country" />
          {requestType === RequestType.Cati && renderCountrySelector(RequestType.Cati)}
        </RadioGroup>
      </FormControl>
      <Box
        display="flex"
        alignItems={{
          xs: 'flex-start',
          sm: 'center',
        }}
        flexDirection={{
          xs: 'column-reverse',
          sm: 'row',
        }}
        gap={1}
        justifyContent="space-between"
        mt={2}
      >
        <Typography component="div" display="flex" alignItems="center" gap={0.5} variant="body2">
          <span>Need something else?</span>
          <Link component="button" type="button" onClick={openChat}>
            Say hi in our live chat!
          </Link>
        </Typography>
        <SubmitButton
          disabled={!canSubmit}
          isDirty={Boolean(requestType)}
          isSubmitting={isSending}
          onClick={handleSubmit}
        >
          Submit Request
        </SubmitButton>
      </Box>
    </Box>
  )
}

export default RequestForm
